import React, { useState } from 'react';
import SEOHead from '@/components/SEOHead';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useInternalAuth } from '@/hooks/useInternalAuth';
import { useUserLocation } from '@/hooks/useUserLocation';
import { useIsMobile } from '@/hooks/use-mobile';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import MobileHeader from '@/components/mobile/MobileHeader';
import MobileBottomNav from '@/components/mobile/MobileBottomNav';
import CountrySelector from '@/components/home/CountrySelector';
import StateNavigation from '@/components/home/StateNavigation';
import LiveGamesSection from '@/components/home/LiveGamesSection';
import RecentResultsSection from '@/components/home/RecentResultsSection';
import OnTheComeUpSection from '@/components/home/OnTheComeUpSection';
import HomeSearchFilters from '@/components/home/HomeSearchFilters';

const Home = () => {
  const { user: supabaseUser, loading: supabaseLoading, initialCheckComplete: supabaseInitialCheck } = useAuth();
  const { user: internalUser, loading: internalLoading, initialCheckComplete: internalInitialCheck } = useInternalAuth();
  const isMobile = useIsMobile();
  
  const {
    viewingStateProvince,
    viewingCountry,
    userStateProvince,
    isViewingHomeLocation,
    setViewingLocation,
    resetToUserLocation,
    loading: locationLoading,
  } = useUserLocation();

  const [searchTerm, setSearchTerm] = useState('');
  const [sportFilter, setSportFilter] = useState<string | null>(null);
  const [ageGroupFilter, setAgeGroupFilter] = useState<string | null>(null);

  // Wait for both auth systems to complete their initial checks
  const bothSystemsReady = supabaseInitialCheck && internalInitialCheck;
  const anySystemLoading = supabaseLoading || internalLoading;

  // Show loading only if we haven't completed initial checks
  if (!bothSystemsReady || anySystemLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-lg text-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Check if user is authenticated
  const isAuthenticated = !!(supabaseUser || internalUser);

  // If not authenticated, redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="flex flex-col min-h-screen bg-background w-full">
      <SEOHead title="Dashboard" description="Your Players Lockeroom dashboard — live games, results, and trending athletes." path="/home" />
      {/* Header */}
      {!isMobile && <Header />}
      {isMobile && <MobileHeader />}
      
      <main className={`flex-grow w-full ${
        isMobile 
          ? 'pt-16 pb-20 px-4' 
          : 'mt-24 mb-6 py-6 px-4 md:px-6'
      }`}>
        <div className="w-full max-w-6xl mx-auto space-y-6">
          {/* Country Selector - Horizontal row of country buttons */}
          <CountrySelector />
          
          {/* State Navigation - Change viewing region */}
          <StateNavigation
            currentState={viewingStateProvince}
            currentCountry={viewingCountry}
            userState={userStateProvince}
            isHomeLocation={isViewingHomeLocation}
            onStateChange={setViewingLocation}
            onResetToHome={resetToUserLocation}
          />
          
          {/* Search and Filters */}
          <HomeSearchFilters
            onSearchChange={setSearchTerm}
            onSportChange={setSportFilter}
            onAgeGroupChange={setAgeGroupFilter}
          />
          
          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left Column - Live Games */}
            <div className="space-y-6">
              <LiveGamesSection
                stateProvince={viewingStateProvince}
                country={viewingCountry}
                sportFilter={sportFilter || undefined}
              />
            </div>
            
            {/* Right Column - Recent Results */}
            <div className="space-y-6">
              <RecentResultsSection
                stateProvince={viewingStateProvince}
                country={viewingCountry}
                sportFilter={sportFilter || undefined}
              />
            </div>
          </div>
          
          {/* On the Come Up - Full Width */}
          <OnTheComeUpSection
            stateProvince={viewingStateProvince}
            country={viewingCountry}
          />
        </div>
      </main>
      
      {/* Footer */}
      {!isMobile && <Footer />}
      {isMobile && <MobileBottomNav />}
    </div>
  );
};

export default Home;
